import { useMemo } from 'react';

import { NAV_GROUP_LABEL_KEY } from '@/features/header/consts/search';
import { useGlobalSearchResults } from '@/features/header/hooks/useGlobalSearchResults';
import { useSearchResults } from '@/features/header/hooks/useSearchResults';
import type { NavSearchRow, SearchSection } from '@/features/header/types/searchResults';

interface UseMergedSearchResultsReturn {
  sections: SearchSection[];
  isFetching: boolean;
  isError: boolean;
  isEmpty: boolean;
}

const NAV_SECTION_ID = 'navigation';

export const useMergedSearchResults = (query: string): UseMergedSearchResultsReturn => {
  const navResults = useSearchResults(query);
  const { sections: entitySections, isFetching, isError: isEntityError } = useGlobalSearchResults(query);

  const navSection = useMemo<SearchSection | undefined>(() => {
    if (navResults.length === 0) {
      return undefined;
    }
    const rows: NavSearchRow[] = navResults.map((result) => ({
      kind: 'nav',
      to: result.to,
      category: result.category,
      label: result.label,
      icon: result.icon
    }));
    return {
      id: NAV_SECTION_ID,
      labelKey: NAV_GROUP_LABEL_KEY,
      fallbackLabel: 'Navigation',
      rows
    };
  }, [navResults]);

  const sections = useMemo(() => {
    const merged: SearchSection[] = [];
    if (navSection) {
      merged.push(navSection);
    }
    merged.push(...entitySections);
    return merged;
  }, [navSection, entitySections]);

  const isEmpty = sections.length === 0;

  // SERP-33: entity search can fail for expected reasons — e.g. a module (PMS) not provisioned
  // for the tenant, or its service not deployed. Navigation results are local and still valid,
  // so surface the error only when there is nothing else to show.
  const isError = isEntityError && !navSection;

  return { sections, isFetching, isError, isEmpty };
};
